import { throttle } from 'lodash-es';

import {
  InactivityEvents,
  InactivityStorageKeys,
} from '~/hooks/useInactivityTracker/InactivityTracker.constants';
import {
  readLastActivity,
  writeLastActivity,
} from '~/hooks/useInactivityTracker/InactivityTracker.utils';

export class InactivityTracker {
  private _timer: number;
  private _timerId: number | undefined;
  private _eventHandler: () => void;
  private _throttledEventHandler: () => void;
  private _trackedEvents: string[] = [
    'load',
    'mousemove',
    'mousedown',
    'scroll',
    'keydown',
    'click',
    // Touch equivalents so activity on touch devices (tablets/phones/webview)
    // resets the timer the same way mouse activity does.
    'touchstart',
    'touchmove',
  ];
  constructor(timeout: number) {
    this._timer = timeout;
    //Event handlers need to be bound, in order to properly set up and remove event listeners
    this._eventHandler = this.syncTimer.bind(this);
    this._throttledEventHandler = throttle(this._eventHandler, 1000);

    //Seed the shared activity timestamp only when it is missing, so a brand-new
    //first tab isn't treated as already idle. We must NOT overwrite an existing
    //timestamp: creating a tracker (tab restore, auth/webview change, refresh)
    //without real user interaction would otherwise reset the shared idle window
    //and weaken the timeout. The timestamp is cleared on logout so a later
    //re-login still starts fresh.
    if (readLastActivity() === null) {
      writeLastActivity();
    }
    this.tracker();
  }

  /**
   * The idle duration (ms) before the inactivity modal should appear.
   */
  get idleTimeout(): number {
    return this._timer;
  }

  /**
   * reset Timer function
   */
  resetTimer(): void {
    clearTimeout(this._timerId);
    this._timerId = window.setTimeout(() => this.handleTimeout(), this._timer);
  }

  /**
   * Called when this tab's local timer elapses. Rather than declaring the user
   * inactive immediately, verify against the shared cross-tab activity
   * timestamp: if any tab saw activity more recently than the timeout, reschedule
   * for the remaining time instead of firing. This prevents a single idle tab
   * from dragging an actively-used tab into the inactivity modal.
   */
  handleTimeout(): void {
    // Clamp to 0 so a backward clock shift (NTP correction, sleep/wake) or a
    // future stored timestamp can't make idleFor negative and reschedule for
    // longer than the configured timeout.
    const idleFor = Math.max(0, Date.now() - this.getLastActivity());
    if (idleFor >= this._timer) {
      this.dispatchUserInactive();
    } else {
      this._timerId = window.setTimeout(
        () => this.handleTimeout(),
        this._timer - idleFor
      );
    }
  }

  /**
   * Sync timer function
   */
  syncTimer(): void {
    this.emitActiveUser();
    this.resetTimer();
  }

  /**
   * Dispatch storage event
   */
  emitActiveUser(): void {
    writeLastActivity();
    try {
      localStorage.setItem(InactivityStorageKeys.userActive, 'true');
      localStorage.removeItem(InactivityStorageKeys.userActive);
    } catch {
      // The cross-tab ping is best-effort. If storage is unavailable (private
      // mode/quota/blocked) the local timer still tracks activity, so ignore
      // the failure rather than break inactivity tracking on every interaction.
    }
  }

  /**
   * Read the shared, cross-tab "last activity" timestamp. Returns 0 when absent
   * so a missing record is treated as idle (fail secure).
   */
  getLastActivity(): number {
    return readLastActivity() ?? 0;
  }

  /**
   * Whether the whole session (across all tabs) has been idle past the timeout.
   * Used on tab refocus to immediately reflect an expired session.
   */
  isSessionIdle(): boolean {
    const last = readLastActivity();
    // When no shared record is available (missing, or storage blocked so the
    // read returns null), we can't confirm the whole session is idle. Defer to
    // the local timer instead of forcing an immediate logout on every refocus.
    if (last === null) {
      return false;
    }
    return Date.now() - last >= this._timer;
  }

  /**
   * dispatch event when the timeout has been reached
   */
  dispatchUserInactive(): void {
    window.dispatchEvent(new CustomEvent(InactivityEvents.inactiveUser));
  }

  /**
   * Sets up events to track in order to measure inactivity
   */
  tracker(): void {
    for (const event of this._trackedEvents) {
      // Listen in the capture phase. React delegates events at the app root and
      // many components call event.stopPropagation() (menus, table row actions,
      // dialogs, tooltips), which stops the native event before it bubbles to
      // window. Capturing guarantees we still see the interaction and reset the
      // timer, so genuine activity never triggers the inactivity modal.
      // passive: true since the handler never calls preventDefault, which avoids
      // scroll/touch performance warnings and main-thread blocking.
      window.addEventListener(event, this._throttledEventHandler, {
        capture: true,
        passive: true,
      });
    }
  }

  /**
   * remove event listeners so that app no longer tracks inactivity
   */
  removeTrackers(): void {
    for (const event of this._trackedEvents) {
      // Must match the capture flag used in tracker() for the removal to work
      // (passive is not part of listener identity, so only capture matters here).
      window.removeEventListener(event, this._throttledEventHandler, {
        capture: true,
      });
    }
    // Cancel any pending inactivity timeout so a discarded tracker (e.g. one
    // replaced when auth/webview state changes) can't later fire
    // dispatchUserInactive and pop the modal while the user is active.
    clearTimeout(this._timerId);
  }
}
